import axios from "axios";
import type { AxiosError, AxiosRequestConfig } from "axios";

import {
  ANONYMOUS_STUBBED_PATHS,
  resolveAnonymousGet,
} from "@/modules/anonymousBrowsing/anonymousApiStubs";

import {
  AUTHENTICATED_ONLY_GET_PATHS,
  isAuthenticatedOnlyGetPath,
} from "./authenticatedOnlyGetPaths";
import { ApiResponseError, logApiError } from "./errorLogging";
import { getApiBaseUrl, getAuthorizationHeader, signedOutUnauthorizedError } from "./index";
import type { GetPaths, GetRequest } from "./types";

const API_PREFIX = "/api/v1";

// The legacy `get(path, args)` keys its anonymous-browsing stubs and signed-out
// fail-fast on the swagger path TEMPLATE. orval hands the mutator the resolved
// URL, so those templates are matched back from it here.
//
// Templates are ordered literal-before-parameter at the first segment where two
// differ, the usual routing precedence: declaration order would otherwise let
// `/promotions/{promotionId}` swallow `/promotions/active-banners` and serve the
// detail stub (`null`) where the banner list stub (`[]`) belongs.
const bySegmentSpecificity = (a: GetPaths, b: GetPaths) => {
  const left = a.split("/");
  const right = b.split("/");
  for (let i = 0; i < Math.min(left.length, right.length); i++) {
    const leftIsParam = left[i].startsWith("{");
    if (leftIsParam !== right[i].startsWith("{")) return leftIsParam ? 1 : -1;
  }
  return right.length - left.length;
};

const GET_TEMPLATES: readonly { path: GetPaths; pattern: RegExp }[] = [
  ...new Set<GetPaths>([...AUTHENTICATED_ONLY_GET_PATHS, ...ANONYMOUS_STUBBED_PATHS]),
]
  .sort(bySegmentSpecificity)
  .map((path) => ({
    path,
    pattern: new RegExp(
      `^${path.replace(/[.*+?^$()|[\]\\]/g, "\\$&").replace(/\{([^}]+)\}/g, "(?<$1>[^/]+)")}$`,
    ),
  }));

const matchGetTemplate = (url: string) => {
  const relative = url.startsWith(API_PREFIX) ? url.slice(API_PREFIX.length) : url;
  for (const { path, pattern } of GET_TEMPLATES) {
    const match = pattern.exec(relative);
    if (match) return { path, params: match.groups ?? {} };
  }
  return undefined;
};

const resolveAnonymous = <P extends GetPaths>(path: P, args: Record<string, unknown>) =>
  resolveAnonymousGet(path, args as GetRequest<P>);

// Unwrap condition intentionally matches the transformer's isEnvelopeSchema
// (`result` + `runInBackground`) so runtime shape and generated type agree.
const isEnvelope = (data: unknown): data is { result: unknown } =>
  data !== null && typeof data === "object" && "result" in data && "runInBackground" in data;

/**
 * Custom mutator for the orval-generated client (see orval.config.ts).
 *
 * Mirrors the legacy src/api/index.ts request layer so migrated call sites keep
 * identical runtime behavior:
 * - base URL from storage / env (`getApiBaseUrl`), `X-App-Version` and the
 *   bearer token via the shared axios instance the legacy layer configures
 * - anonymous-browsing stubs and the signed-out local 401 on GETs
 * - `Cache-Control: no-cache` on GETs
 * - array query params serialized as repeated `key=value` pairs (ASP.NET binding)
 * - 204 → null; BaseCommand `{ result }` envelopes unwrapped (the orval input
 *   transformer rewrites those response schemas to match)
 *
 * `options` is the generated function's trailing axios-config argument — the
 * orval door for the per-call headers / responseType the legacy `rawGet` and
 * `rawPostText` helpers exist for.
 */
export const customAxios = async <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const method = (config.method ?? "GET").toUpperCase();
  const path = config.url ?? "";
  const url = `${getApiBaseUrl()}${path}`;
  const requestData = method === "GET" ? config.params : config.data;

  if (method === "GET") {
    const template = matchGetTemplate(path);
    if (template) {
      const anonymous = resolveAnonymous(template.path, { ...template.params, ...config.params });
      if (anonymous) return anonymous.value as T;
      if (getAuthorizationHeader() === undefined && isAuthenticatedOnlyGetPath(template.path)) {
        throw signedOutUnauthorizedError(url);
      }
    }
  }

  try {
    const { data, status } = await axios.request<unknown>({
      ...config,
      ...options,
      url,
      headers: {
        ...(method === "GET" ? { "Cache-Control": "no-cache" } : {}),
        ...config.headers,
        ...options?.headers,
      },
      paramsSerializer: { indexes: null },
    });

    if (status === 204) return null as T;

    if (isEnvelope(data)) {
      if (method === "GET" && data.result == null) {
        throw new ApiResponseError(`Failed to load api get result for ${path}`, data, status);
      }
      return data.result as T;
    }

    // A 200 GET whose body is not an object (an HTML shell, a bare string) is
    // not a payload; the legacy `get` rejects it rather than caching it.
    if (method === "GET" && (data === null || typeof data !== "object")) {
      throw new ApiResponseError(`Failed to load api get result for ${path}`, data, status);
    }
    return data as T;
  } catch (error) {
    logApiError(
      method,
      path,
      url,
      requestData ?? {},
      method === "GET" ? "queryParams" : "requestPayload",
      error,
    );
    throw error;
  }
};

export type ErrorType<E> = AxiosError<E>;

export default customAxios;
